import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { IconButton } from './IconButton'

let offeneBlaetter = 0
let vorherigerUeberlauf = ''
let vorherigerRand = ''

function sperreSeite(): () => void {
  if (offeneBlaetter === 0) {
    vorherigerUeberlauf = document.body.style.overflow
    vorherigerRand = document.body.style.paddingInlineEnd
    const scrollbar = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (scrollbar > 0) document.body.style.paddingInlineEnd = `${scrollbar}px`
  }
  offeneBlaetter++
  return () => {
    offeneBlaetter = Math.max(0, offeneBlaetter - 1)
    if (offeneBlaetter === 0) {
      document.body.style.overflow = vorherigerUeberlauf
      document.body.style.paddingInlineEnd = vorherigerRand
    }
  }
}

/**
 * Ein Blatt, das von unten kommt (ab mittlerer Breite ein Dialog in der Mitte).
 *
 * ⚠️ Innen liegt ein natives `<dialog>` mit `showModal()`. Das bringt
 * Fokusfalle, Escape und die Verdunklung mit — alles Dinge, die selbst gebaut
 * regelmäßig halb fertig bleiben.
 *
 * Geschlossen rendert es NICHTS: sonst läge in jeder Seite totes Markup, das
 * Sprachausgaben durchwandern und Suchfunktionen finden.
 *
 * ⚠️ Beim Schließen bleibt es so lange stehen, bis seine Abgangsbewegung durch
 * ist — sonst nimmt React den Knoten im selben Bild aus dem Baum, und es gibt
 * nichts zu sehen. Gewartet wird auf das EREIGNIS `animationend`, nicht auf
 * eine Zahl: eine Dauer in JavaScript, die die Dauer in CSS wiederholt, ist
 * die zweite Wahrheit — und bei „weniger Bewegung" wäre sie sofort falsch.
 */
export function Sheet({
  offen,
  titel,
  onSchliessen,
  children,
}: {
  offen: boolean
  titel: string
  onSchliessen: () => void
  children: ReactNode
}) {
  const id = useId()
  const dialog = useRef<HTMLDialogElement>(null)
  const ausloeser = useRef<HTMLElement | null>(null)
  const [imBaum, setImBaum] = useState(offen)
  const [geht, setGeht] = useState(false)

  useEffect(() => {
    if (offen) {
      setGeht(false)
      setImBaum(true)
      return
    }
    // Nur wenn überhaupt etwas dasteht, gibt es einen Abgang abzuspielen.
    setImBaum((steht) => {
      if (steht) setGeht(true)
      return steht
    })
  }, [offen])

  useEffect(() => {
    const d = dialog.current
    if (d && imBaum && !geht && !d.open) {
      ausloeser.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null
      d.showModal()
    }
  }, [imBaum, geht])

  useEffect(() => {
    if (!imBaum) return
    return sperreSeite()
  }, [imBaum])

  if (!imBaum) return null

  const abgangFertig = () => {
    if (!geht) return
    dialog.current?.close()
    setGeht(false)
    setImBaum(false)
    requestAnimationFrame(() => ausloeser.current?.focus())
  }

  return (
    <dialog
      className="blatt"
      ref={dialog}
      {...(geht ? { 'data-geht': '' } : {})}
      aria-labelledby={id}
      aria-modal="true"
      onClose={onSchliessen}
      // ⚠️ Escape schließt ein natives `<dialog>` SOFORT und selbst - dann ist
      // der Knoten schon zu, `[open]` trifft nicht mehr, und es gibt nichts
      // abzuspielen. Der Abgang wird deshalb hier übernommen; geschlossen
      // wird erst, wenn die Bewegung durch ist.
      onCancel={(e) => {
        e.preventDefault()
        onSchliessen()
      }}
      onAnimationEnd={abgangFertig}
      onClick={(e) => {
        // Ein Klick auf die Verdunklung trifft den Dialog selbst; ein Klick
        // auf den Inhalt trifft ein Kind. Nur der erste schließt.
        if (e.target === dialog.current) onSchliessen()
      }}
    >
      <div className="blatt__kopf">
        <h2 className="blatt__titel" id={id}>
          {titel}
        </h2>
        <IconButton name="close" label="Schließen" onClick={onSchliessen} />
      </div>
      <div className="blatt__inhalt">{children}</div>
    </dialog>
  )
}
